import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  CalendarCheck, RefreshCw, Download, X, User, BedDouble,
  Euro, Building2, Calendar, History
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

const STATUS_META = {
  pending:        { label: "En attente",          cls: "bg-amber-50 text-amber-600" },
  confirmed:      { label: "Confirmée",            cls: "bg-emerald-50 text-emerald-700" },
  completed:      { label: "Terminée",             cls: "bg-blue-50 text-blue-600" },
  cancelled:      { label: "Annulée",              cls: "bg-gray-100 text-gray-500" },
  pending_cancel: { label: "Annulation en cours",  cls: "bg-orange-50 text-orange-600" },
  no_show:        { label: "No-show",              cls: "bg-red-50 text-red-500" },
};

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR");
const fmtEur  = (v) => `${(v || 0).toLocaleString("fr-FR")} €`;

const REQUESTED_BY = {
  guest: "le voyageur",
  hotel: "l'hébergeur",
  admin: "l'hébergeur",
};

function StatusPill({ status }) {
  const s = STATUS_META[status] || { label: status, cls: "bg-gray-100 text-gray-500" };
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

function ReservationDrawer({ reservation, onClose }) {
  const r = reservation;
  const mods = r.modifications || [];

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-screen w-[420px] bg-white border-l border-[#e5e7eb] z-50 overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb] sticky top-0 bg-white">
          <div>
            <p className="text-sm font-medium text-gray-900">Réservation</p>
            <p className="text-[11px] text-gray-400 font-mono">{r.reservationId}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 transition">
            <X size={15} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <StatusPill status={r.status} />
            <p className="text-[11px] text-gray-400">
              Créée le {fmtDate(r.createdAt)}
            </p>
          </div>

          {/* Voyageur */}
          <div className="bg-[#f9fafb] border border-[#e5e7eb] rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <User size={13} /><p className="text-[11px] font-medium uppercase tracking-widest">Voyageur</p>
            </div>
            <p className="text-sm font-medium text-gray-900">{r.guest?.firstName} {r.guest?.lastName}</p>
            <p className="text-xs text-gray-500 mt-0.5">{r.guest?.email}</p>
            {r.guest?.phone && <p className="text-xs text-gray-500">{r.guest.phone}</p>}
            <p className="text-xs text-gray-400 mt-1">
              {r.guests?.adults ?? 1} adulte{(r.guests?.adults ?? 1) > 1 ? "s" : ""}
              {r.guests?.children > 0 ? `, ${r.guests.children} enfant${r.guests.children > 1 ? "s" : ""}` : ""}
            </p>
          </div>

          {/* Séjour */}
          <div className="bg-[#f9fafb] border border-[#e5e7eb] rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <Building2 size={13} /><p className="text-[11px] font-medium uppercase tracking-widest">Séjour</p>
            </div>
            <p className="text-sm font-medium text-gray-900">{r.hotelId?.name || "Hôtel supprimé"}</p>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
              <BedDouble size={12} /> {r.roomId?.title || "—"}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
              <Calendar size={12} />
              {fmtDate(r.checkInDate)} → {fmtDate(r.checkOutDate)}
              {r.totalNights ? ` · ${r.totalNights} nuit${r.totalNights > 1 ? "s" : ""}` : ""}
            </div>
            {r.ratePlanSnapshot?.name && (
              <p className="text-xs text-gray-400 mt-1">Tarif : {r.ratePlanSnapshot.name}</p>
            )}
          </div>

          {/* ✅ MODIFICATIONS — piste d'audit.
              C'est ici qu'on répond à un hébergeur qui conteste le montant
              de sa commission : chaque changement de dates est daté, avec
              l'ancienne et la nouvelle assiette. */}
          {r.modificationCount > 0 && (
            <div className="bg-[#f9fafb] border border-[#e5e7eb] rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-3">
                <History size={13} />
                <p className="text-[11px] font-medium uppercase tracking-widest">
                  {r.modificationCount} modification{r.modificationCount > 1 ? "s" : ""}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {mods.map((m, i) => {
                  const delta = (m.to?.totalPrice || 0) - (m.from?.totalPrice || 0);
                  return (
                    <div key={i} className={i > 0 ? "pt-3 border-t border-[#e5e7eb]" : ""}>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        <span className="line-through text-gray-400">
                          {fmtDate(m.from?.checkInDate)} → {fmtDate(m.from?.checkOutDate)}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span className="font-medium text-gray-900">
                          {fmtDate(m.to?.checkInDate)} → {fmtDate(m.to?.checkOutDate)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-[11px] text-gray-400">Total</p>
                        <p className="text-[11px] text-gray-500">
                          {fmtEur(m.from?.totalPrice)} → <span className="font-medium text-gray-900">{fmtEur(m.to?.totalPrice)}</span>
                          {delta !== 0 && (
                            <span className={delta > 0 ? "text-emerald-600 ml-1" : "text-orange-500 ml-1"}>
                              ({delta > 0 ? "+" : ""}{delta.toLocaleString("fr-FR")} €)
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center justify-between mt-0.5">
                        <p className="text-[11px] text-gray-400">Commission</p>
                        <p className="text-[11px] text-gray-500">
                          {fmtEur(m.from?.platformFee)} → <span className="font-medium text-[var(--k-green)]">{fmtEur(m.to?.platformFee)}</span>
                        </p>
                      </div>

                      <p className="text-[11px] text-gray-300 mt-1.5">
                        {m.appliedAt ? new Date(m.appliedAt).toLocaleString("fr-FR") : "—"}
                        {m.requestedBy && ` · à la demande de ${REQUESTED_BY[m.requestedBy] || m.requestedBy}`}
                      </p>
                      {m.reason && (
                        <p className="text-[11px] text-gray-400 italic mt-0.5">« {m.reason} »</p>
                      )}
                    </div>
                  );
                })}

                {mods.length === 0 && (
                  <p className="text-[11px] text-gray-400">
                    Détail indisponible — le compteur indique {r.modificationCount} changement{r.modificationCount > 1 ? "s" : ""}.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Finances */}
          <div className="bg-[#f9fafb] border border-[#e5e7eb] rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <Euro size={13} /><p className="text-[11px] font-medium uppercase tracking-widest">Finances</p>
            </div>
            <div className="flex flex-col gap-1.5">
              {[
                { label: "Prix total", value: fmtEur(r.totalPrice) },
                { label: "Commission Komorotel", value: fmtEur(r.platformFee), accent: true },
                { label: "Reversement hôte", value: fmtEur(r.hostPayout) },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">{item.label}</p>
                  <p className={`text-sm font-medium ${item.accent ? "text-[var(--k-green)]" : "text-gray-900"}`}>{item.value}</p>
                </div>
              ))}
            </div>
            {r.modificationCount > 0 && (
              <p className="text-[11px] text-gray-400 mt-2 pt-2 border-t border-[#e5e7eb]">
                Montants recalculés après modification des dates.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function SuperAdminReservations() {
  const [reservations, setReservations] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [filters, setFilters] = useState({
    hotelId: "", status: "", island: "", from: "", to: "",
  });

  const fetchHotels = () => {
    axios.get(`${API}/hotels`, { withCredentials: true })
      .then(r => setHotels(r.data))
      .catch(console.error);
  };

  const fetchReservations = () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    axios.get(`${API}/reservations`, { params, withCredentials: true })
      .then(r => setReservations(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHotels(); }, []);
  useEffect(() => { fetchReservations(); }, [filters]);

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  const totals = useMemo(() => ({
    gross: reservations.reduce((s, r) => s + (r.totalPrice || 0), 0),
    fees: reservations.reduce((s, r) => s + (r.platformFee || 0), 0),
    modified: reservations.filter(r => r.modificationCount > 0).length,
  }), [reservations]);

  const exportCSV = () => {
    const headers = [
      "Référence", "Hôtel", "Île", "Voyageur", "Email",
      "Arrivée", "Départ", "Nuits", "Total €", "Commission €",
      "Modifications", "Séjour initial", "Commission initiale €",
      "Statut"
    ];
    const rows = reservations.map(r => {
      // ✅ Le premier élément conserve l'état d'origine de la réservation :
      //    c'est la référence en cas de contestation de facturation.
      const first = (r.modifications || [])[0];
      return [
        r.reservationId,
        r.hotelId?.name || "",
        r.hotelId?.island || "",
        `${r.guest?.firstName || ""} ${r.guest?.lastName || ""}`.trim(),
        r.guest?.email || "",
        fmtDate(r.checkInDate),
        fmtDate(r.checkOutDate),
        r.totalNights || "",
        r.totalPrice || 0,
        r.platformFee || 0,
        r.modificationCount || 0,
        first ? `${fmtDate(first.from?.checkInDate)} → ${fmtDate(first.from?.checkOutDate)}` : "",
        first ? (first.from?.platformFee ?? "") : "",
        STATUS_META[r.status]?.label || r.status,
      ];
    });
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map(row => row.map(escape).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reservations-komorotel-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-5">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Réservations</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {reservations.length} réservation{reservations.length > 1 ? "s" : ""} ·
            {" "}{totals.gross.toLocaleString("fr-FR")} € de volume · {totals.fees.toLocaleString("fr-FR")} € de commission
            {totals.modified > 0 && ` · ${totals.modified} modifiée${totals.modified > 1 ? "s" : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchReservations}
            className="w-9 h-9 bg-white border border-[#e5e7eb] rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={exportCSV}
            disabled={reservations.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--k-green)] text-white text-sm font-medium rounded-xl hover:bg-[var(--k-green-hover)] transition disabled:opacity-50"
          >
            <Download size={15} />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── FILTRES ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={filters.hotelId}
          onChange={e => setFilter("hotelId", e.target.value)}
          className="px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-600 max-w-[200px]"
        >
          <option value="">Tous les hôtels</option>
          {hotels.map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
        </select>

        <select
          value={filters.status}
          onChange={e => setFilter("status", e.target.value)}
          className="px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-600"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <select
          value={filters.island}
          onChange={e => setFilter("island", e.target.value)}
          className="px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-600"
        >
          <option value="">Toutes les îles</option>
          <option value="Ngazidja">Ngazidja</option>
          <option value="Anjouan">Anjouan</option>
          <option value="Moheli">Mohéli</option>
        </select>

        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={filters.from}
            onChange={e => setFilter("from", e.target.value)}
            className="px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-600"
          />
          <span className="text-xs text-gray-400">→</span>
          <input
            type="date"
            value={filters.to}
            onChange={e => setFilter("to", e.target.value)}
            className="px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-600"
          />
        </div>

        {(filters.hotelId || filters.status || filters.island || filters.from || filters.to) && (
          <button
            onClick={() => setFilters({ hotelId: "", status: "", island: "", from: "", to: "" })}
            className="text-xs text-gray-400 hover:text-gray-600 underline transition"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* ── TABLE ── */}
      <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e5e7eb] text-left">
                {["Référence", "Hôtel", "Voyageur", "Séjour", "Total", "Commission", "Statut"].map(h => (
                  <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
              ) : reservations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <CalendarCheck className="text-gray-300 mx-auto mb-2" size={24} />
                    <p className="text-xs text-gray-400">Aucune réservation pour ces filtres.</p>
                  </td>
                </tr>
              ) : reservations.map(r => (
                <tr
                  key={r._id}
                  onClick={() => setSelected(r)}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer transition duration-150"
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{r.reservationId}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 whitespace-nowrap">{r.hotelId?.name || "—"}</p>
                    <p className="text-[11px] text-gray-400">{r.hotelId?.island}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-700 whitespace-nowrap">{r.guest?.firstName} {r.guest?.lastName}</p>
                    <p className="text-[11px] text-gray-400 truncate max-w-[160px]">{r.guest?.email}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {fmtDate(r.checkInDate)} → {fmtDate(r.checkOutDate)}
                    {/* ✅ Marqueur : les dates ne sont plus celles d'origine */}
                    {r.modificationCount > 0 && (
                      <span className="ml-1.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                        modifiée{r.modificationCount > 1 ? ` ×${r.modificationCount}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{fmtEur(r.totalPrice)}</td>
                  <td className="px-4 py-3 font-medium text-[var(--k-green)] whitespace-nowrap">{fmtEur(r.platformFee)}</td>
                  <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <ReservationDrawer reservation={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}